import React, { useState } from "react";
import { useQuery } from "@apollo/client";
import {
  MdBolt,
  MdContentCut,
  MdCurrencyRupee,
  MdDiamond,
  MdEmojiEvents,
  MdFavorite,
  MdInfoOutline,
  MdLocalFireDepartment,
  MdMilitaryTech,
  MdStar,
  MdTrendingUp,
  MdWhatshot,
} from "react-icons/md";
import { GET_GAME_RULES } from "./graphql";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
const count = (n) => Number(n || 0).toLocaleString("en-IN");

const TITLE_NAMES = {
  ROOKIE: "Rookie",
  RISING_STAR: "Rising Star",
  PRO: "Pro",
  EXPERT: "Expert",
  MASTER: "Master",
  LEGEND: "Legend",
};

const TITLE_TONES = {
  ROOKIE: "bg-gray-100 text-gray-700",
  RISING_STAR: "bg-sky-50 text-sky-700",
  PRO: "bg-violet-50 text-violet-700",
  EXPERT: "bg-emerald-50 text-emerald-700",
  MASTER: "bg-amber-50 text-amber-700",
  LEGEND: "bg-rose-50 text-rose-700",
};

const BADGE_LOOK = {
  TOP_EARNER: { Icon: MdLocalFireDepartment, cls: "from-orange-400 to-red-500" },
  FIVE_STAR_PRO: { Icon: MdStar, cls: "from-yellow-300 to-amber-500" },
  SPEED_MASTER: { Icon: MdBolt, cls: "from-sky-400 to-blue-600" },
  CUSTOMER_FAVOURITE: { Icon: MdFavorite, cls: "from-fuchsia-400 to-violet-600" },
  TOP_3: { Icon: MdEmojiEvents, cls: "from-yellow-300 to-yellow-600" },
  STREAK_MASTER: { Icon: MdWhatshot, cls: "from-rose-400 to-red-600" },
  LAKH_CLUB: { Icon: MdDiamond, cls: "from-emerald-300 to-emerald-700" },
};

const SCOPE_LABEL = {
  DAY: "In one day",
  MONTH: "In a calendar month",
  ALL_TIME: "All time",
  STREAK: "Days in a row",
};

// Plain-English mission, matching how the app words it.
const missionText = (m) => {
  switch (m.metric) {
    case "REVENUE":
      return `Complete bookings worth ${inr(m.target)} in the day`;
    case "FIVE_STARS":
      return m.target > 1 ? `Get ${m.target} five-star ratings on the day's bookings` : "Get a five-star rating on a booking done that day";
    case "BIG_BOOKING":
      return `Complete one booking of ${inr(m.target)} or more`;
    default:
      return `Complete ${count(m.target)} services in the day`;
  }
};

const MISSION_ICON = {
  BOOKINGS: MdContentCut,
  REVENUE: MdCurrencyRupee,
  FIVE_STARS: MdStar,
  BIG_BOOKING: MdDiamond,
};

const badgeTarget = (b) => {
  switch (b.unit) {
    case "RUPEES":
      return inr(b.target);
    case "PERCENT":
      return `${b.target}% five-star${b.minRatings ? ` (min ${b.minRatings} ratings)` : ""}`;
    case "RANK":
      return `Top ${b.target}`;
    case "DAYS":
      return `${b.target} days`;
    default:
      return count(b.target);
  }
};

const Section = ({ title, subtitle, children, right }) => (
  <section className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
    <div className="flex items-start justify-between gap-4 mb-4">
      <div>
        <h2 className="text-base font-semibold text-gray-900">{title}</h2>
        {subtitle && <p className="text-sm text-gray-500 mt-0.5">{subtitle}</p>}
      </div>
      {right}
    </div>
    {children}
  </section>
);

/**
 * Read-only explanation of the game layer (XP, levels, daily missions,
 * badges) so ops can see the rules without reading code. Every number
 * comes from the backend's own constants (getGameRules).
 */
const GameRulesTab = () => {
  const { data, loading, error } = useQuery(GET_GAME_RULES, { fetchPolicy: "network-only" });
  const [showAllLevels, setShowAllLevels] = useState(false);
  const r = data?.getGameRules;

  if (loading) return <div className="py-16 text-center text-gray-400">Loading…</div>;
  if (error || !r) return <div className="py-16 text-center text-red-400">Could not load the game rules.</div>;

  const { xp } = r;
  const sampleValue = Math.min(Math.floor(1500 / 100) * xp.per100Rupees, xp.valueCap);
  const sampleTotal = xp.perBooking + sampleValue + xp.fiveStar;
  const missionMax = r.missions.reduce((s, m) => s + m.xp, 0);

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-3 rounded-xl border border-violet-300/30 bg-violet-500/10 px-4 py-3 text-sm text-violet-100">
        <MdInfoOutline className="text-lg mt-0.5 shrink-0" />
        <div>
          <span className="font-semibold text-white">XP is points, not money.</span> It moves professionals up levels and
          unlocks badges. Money comes only from the coin rules on the <span className="font-semibold text-white">Rules</span> tab.
          These rules apply to everyone; this page is read-only.
        </div>
      </div>

      <Section title="How XP is earned" subtitle="Counted automatically from completed bookings - nothing to approve.">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {[
            { Icon: MdContentCut, tone: "bg-violet-50 text-violet-600", value: `+${xp.perBooking} XP`, label: "Every completed booking" },
            {
              Icon: MdCurrencyRupee,
              tone: "bg-emerald-50 text-emerald-600",
              value: `+${xp.per100Rupees} XP / ₹100`,
              label: `Of the booking's value, up to ${xp.valueCap} XP per booking`,
            },
            { Icon: MdStar, tone: "bg-amber-50 text-amber-600", value: `+${xp.fiveStar} XP`, label: "Five-star rating from the customer" },
            { Icon: MdStar, tone: "bg-gray-100 text-gray-500", value: `+${xp.fourStar} XP`, label: "Four-star rating" },
          ].map((c) => (
            <div key={c.label} className="rounded-lg border border-gray-100 p-3">
              <span className={`h-9 w-9 rounded-lg flex items-center justify-center text-lg ${c.tone}`}>
                <c.Icon />
              </span>
              <div className="mt-2 text-lg font-bold text-gray-900">{c.value}</div>
              <div className="text-xs text-gray-500 leading-snug">{c.label}</div>
            </div>
          ))}
        </div>
        <div className="mt-3 rounded-lg bg-gray-50 px-3 py-2.5 text-sm text-gray-700">
          <span className="font-semibold text-gray-900">Example:</span> a {inr(1500)} booking rated five-star ={" "}
          {xp.perBooking} + {sampleValue} + {xp.fiveStar} = <span className="font-semibold text-violet-700">{sampleTotal} XP</span>.
          Daily missions add up to <span className="font-semibold text-violet-700">{count(missionMax)} XP</span> more per day.
        </div>
      </Section>

      <Section title="Levels" subtitle={`${r.levelFormula}. Each level needs a little more XP than the last.`}>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
          {r.titles.map((t, i) => {
            const next = r.titles[i + 1];
            return (
              <div key={t.code} className="rounded-lg border border-gray-100 p-3">
                <span className={`inline-block text-xs font-semibold rounded-full px-2 py-0.5 ${TITLE_TONES[t.code] || ""}`}>
                  {TITLE_NAMES[t.code] || t.code}
                </span>
                <div className="mt-2 text-sm font-semibold text-gray-900">
                  Level {t.fromLevel}
                  {next ? `–${next.fromLevel - 1}` : "+"}
                </div>
                <div className="text-xs text-gray-500">from {count(t.totalXp)} XP</div>
              </div>
            );
          })}
        </div>
        <button onClick={() => setShowAllLevels((v) => !v)} className="mt-3 text-sm font-medium text-violet-700 hover:underline">
          {showAllLevels ? "Hide" : "Show"} XP for every level
        </button>
        {showAllLevels && (
          <div className="mt-2 overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="text-left text-xs uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-3 py-2 font-medium">Level</th>
                  <th className="px-3 py-2 font-medium">Title</th>
                  <th className="px-3 py-2 font-medium text-right">Total XP to reach</th>
                  <th className="px-3 py-2 font-medium text-right">XP to next level</th>
                </tr>
              </thead>
              <tbody>
                {r.levels.map((l) => (
                  <tr key={l.level} className="border-t border-gray-100">
                    <td className="px-3 py-1.5 font-medium text-gray-900">{l.level}</td>
                    <td className="px-3 py-1.5 text-gray-600">{TITLE_NAMES[l.title] || l.title}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{count(l.totalXp)}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-gray-500">{count(l.xpToNext)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      <Section
        title="Daily missions"
        subtitle="Same for every professional. Reset at midnight (India time). Each one pays its XP once per day."
        right={
          <span
            className={`text-xs font-medium rounded-full px-2.5 py-1 whitespace-nowrap ${
              r.missionsSource === "CONFIG" ? "bg-amber-50 text-amber-700" : "bg-gray-100 text-gray-600"
            }`}
          >
            {r.missionsSource === "CONFIG" ? "From system config" : "Default missions"}
          </span>
        }
      >
        <div className="divide-y divide-gray-100">
          {r.missions.map((m) => {
            const Icon = MISSION_ICON[m.metric] || MdTrendingUp;
            return (
              <div key={m.code} className="flex items-center gap-3 py-2.5">
                <span className="h-9 w-9 rounded-full bg-violet-50 text-violet-600 flex items-center justify-center text-lg shrink-0">
                  <Icon />
                </span>
                <div className="flex-1 text-sm text-gray-900">{missionText(m)}</div>
                <span className="text-sm font-bold text-amber-700 bg-amber-50 rounded-full px-2.5 py-0.5">+{m.xp} XP</span>
              </div>
            );
          })}
        </div>
        <p className="mt-3 text-xs text-gray-500">
          To change missions without an app release, set the <code className="px-1 bg-gray-100 rounded">GAME_MISSIONS</code> key in
          system configs (same fields: code, metric, target, xp). Metrics: BOOKINGS, REVENUE, FIVE_STARS, BIG_BOOKING.
        </p>
      </Section>

      {r.eotm && (
        <Section
          title="Employee of the Month"
          subtitle={`Score out of 100 for each calendar month. Needs at least ${r.eotm.minBookings} completed bookings. You confirm the winner on the Employee of the Month tab.`}
        >
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
            {[
              { label: "Booking value", pts: r.eotm.value, how: "Compared with the month's highest value", cls: "bg-violet-500" },
              { label: "Rating", pts: r.eotm.rating, how: "Average rating (3★ = 0, 5★ = full); full weight from 10 ratings", cls: "bg-amber-400" },
              { label: "On-time starts", pts: r.eotm.punctuality, how: `Share of bookings started within ${r.eotm.onTimeMinutes} min of the slot`, cls: "bg-sky-500" },
              { label: "Steps followed", pts: r.eotm.steps, how: "Share with Reached, Start and Scan all marked (Scan only when products were used)", cls: "bg-emerald-500" },
              { label: "Deductions", pts: -r.eotm.perDeduction, how: "Per step deduction that month (refunded ones don't count)", cls: "bg-red-500" },
            ].map((p) => (
              <div key={p.label} className="rounded-lg border border-gray-100 p-3">
                <span className={`inline-block h-2 w-8 rounded-full ${p.cls}`} />
                <div className="mt-2 text-lg font-bold text-gray-900">
                  {p.pts > 0 ? `${p.pts} pts` : `${p.pts} each`}
                </div>
                <div className="text-sm font-medium text-gray-800">{p.label}</div>
                <div className="text-xs text-gray-500 leading-snug mt-0.5">{p.how}</div>
              </div>
            ))}
          </div>
          <p className="mt-3 text-xs text-gray-500">
            The winner gets the coin prize you set, an Employee of the Month badge and a celebration in the app.
          </p>
        </Section>
      )}

      <Section title="Badges" subtitle="Unlocked automatically and never taken away. Locked badges show the professional how close they are.">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {r.badges.map((b) => {
            const look = BADGE_LOOK[b.code] || { Icon: MdMilitaryTech, cls: "from-gray-300 to-gray-500" };
            return (
              <div key={b.code} className="rounded-lg border border-gray-100 p-3 flex gap-3">
                <span className={`h-12 w-12 rounded-full bg-gradient-to-br ${look.cls} text-white text-2xl flex items-center justify-center shrink-0 shadow-sm`}>
                  <look.Icon />
                </span>
                <div className="min-w-0">
                  <div className="font-semibold text-gray-900">{b.name}</div>
                  <div className="text-xs text-gray-600 leading-snug mt-0.5">{b.description}</div>
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    <span className="text-[11px] font-semibold rounded-full px-2 py-0.5 bg-violet-50 text-violet-700">{badgeTarget(b)}</span>
                    <span className="text-[11px] rounded-full px-2 py-0.5 bg-gray-100 text-gray-600">{SCOPE_LABEL[b.scope] || b.scope}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </Section>
    </div>
  );
};

export default GameRulesTab;
